import { useCallback, useMemo, useState } from "react";
import { useAuth } from "@/shared/contexts/AuthContext";
import {
	applyVisibility,
	columnStorageKey,
	minWidthFor,
	readHiddenColumns,
	toggleHidden,
	writeHiddenColumns,
	type ColumnSpec,
} from "../utils/columnVisibility";

const storage = (): Storage | null => {
	try {
		return typeof window === "undefined" ? null : window.localStorage;
	} catch {
		return null;
	}
};

/** Column show/hide state for one admin table, remembered in this browser for the signed-in user. */
export const useColumnVisibility = (tableKey: string, columns: ColumnSpec[], defaultHidden: string[] = []) => {
	const { user } = useAuth();
	const key = columnStorageKey(tableKey, user?.uuid);
	const [hidden, setHidden] = useState<string[]>(() => readHiddenColumns(storage(), key) ?? defaultHidden);

	const toggle = useCallback(
		(columnKey: string) =>
			setHidden((current) => {
				const next = toggleHidden(current, columnKey, columns);
				writeHiddenColumns(storage(), key, next);
				return next;
			}),
		[columns, key],
	);
	const reset = useCallback(() => {
		setHidden([]);
		writeHiddenColumns(storage(), key, []);
	}, [key]);

	const visible = useMemo(() => applyVisibility(columns, hidden), [columns, hidden]);
	return { hidden, toggle, reset, visible, minWidth: minWidthFor(visible) };
};
